import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';
import { UserData } from '../types/auth';
import { UserRole } from '../types/users';

interface AuthContextType {
  user: {
    isAuthenticated: boolean;
    role: UserRole | null;
    data: UserData | null;
  };
  login: (userData: { role: UserRole; data: UserData }) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  // Initialize state from localStorage
  const [user, setUser] = useState<AuthContextType['user']>(() => {
    const sessionUser = localStorage.getItem('user');
    return sessionUser
      ? { isAuthenticated: true, ...JSON.parse(sessionUser) }
      : { isAuthenticated: false, role: null, data: null };
  });

  // Sync localStorage changes across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user') {
        if (e.newValue) {
          setUser({ isAuthenticated: true, ...JSON.parse(e.newValue) });
        } else {
          setUser({ isAuthenticated: false, role: null, data: null });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = ({ role, data }: { role: UserRole; data: UserData }) => {
    const userData = { isAuthenticated: true, role, data };
    setUser(userData);
    localStorage.setItem('user', JSON.stringify({ role, data }));
  };

  const logout = () => {
    setUser({ isAuthenticated: false, role: null, data: null });
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
